import { errAsync, ResultAsync } from "neverthrow";
import { useEffect, useRef, useState } from "react";

const CANVAS_WIDTH = 64;
const CANVAS_HEIGHT = 64;
const REFRESH_INTERVAL_MS = 1000;

type DebugScreenAppProps = {
  heading?: string;
  publicDeviceId: string;
};

type FetchScreenFrameArgs = {
  publicDeviceId: string;
};

type RenderFrameArgs = {
  buffer: Uint8Array;
  context: CanvasRenderingContext2D;
  height: number;
  width: number;
};

/*
 * Retrieves the latest debug screen frame from the server API.
 * Inputs: args.publicDeviceId - the identifier used to request debug screen data.
 * Outputs: A ResultAsync resolving to a Uint8Array containing RGB565 pixel data.
 */
const fetchScreenFrame = ({ publicDeviceId }: FetchScreenFrameArgs) =>
  ResultAsync.fromPromise(
    fetch(`/api/screen/${publicDeviceId}`),
    (error) => ({ code: "DEBUG_SCREEN_FETCH_FAILED", error })
  )
    .andThen((response) => {
      if (!response.ok) {
        return errAsync({
          code: "DEBUG_SCREEN_FETCH_FAILED",
          error: new Error(`Request failed with status ${response.status}`),
          status: response.status,
        });
      }

      return ResultAsync.fromPromise(
        response.arrayBuffer(),
        (error) => ({ code: "DEBUG_SCREEN_BUFFER_FAILED", error })
      ).map((buffer) => new Uint8Array(buffer));
    });

/*
 * Converts the raw RGB565 buffer into ImageData and writes it to the canvas context.
 * Inputs: args.context - the 2D canvas context, args.buffer - raw pixel data, args.width/args.height - canvas dimensions.
 * Outputs: Draws the frame directly to the provided context and returns void.
 */
const renderFrame = ({ context, buffer, width, height }: RenderFrameArgs) => {
  const image = context.createImageData(width, height);
  const totalPixels = width * height;

  for (let index = 0; index < totalPixels; index += 1) {
    const byteOffset = index * 2;
    const lowerByte = buffer[byteOffset] ?? 0;
    const upperByte = buffer[byteOffset + 1] ?? 0;
    const value = lowerByte | (upperByte << 8);
    image.data[index * 4] = ((value >> 11) & 0x1F) << 3;
    image.data[index * 4 + 1] = ((value >> 5) & 0x3F) << 2;
    image.data[index * 4 + 2] = (value & 0x1F) << 3;
    image.data[index * 4 + 3] = 255;
  }

  context.putImageData(image, 0, 0);
};

/*
 * Provides a live preview canvas that polls the debug screen endpoint on an interval.
 * Inputs: props.publicDeviceId - the remote device identifier to poll.
 * Outputs: React component tree showing the debug canvas alongside status messaging.
 */
export const DebugScreenApp = (props: DebugScreenAppProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    const context = (() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error("Canvas element not found", { code: "DEBUG_CANVAS_MISSING" });
        return undefined;
      }

      const twoDimensionalContext = canvas.getContext("2d");
      if (!twoDimensionalContext) {
        console.error("Unable to initialise 2D context", { code: "DEBUG_CANVAS_CONTEXT_FAILED" });
        return undefined;
      }

      twoDimensionalContext.imageSmoothingEnabled = false;
      canvas.style.imageRendering = "pixelated";

      return twoDimensionalContext;
    })();

    if (!context) {
      return undefined;
    }

    let isCancelled = false;

    const renderOnce = () => {
      void fetchScreenFrame({ publicDeviceId: props.publicDeviceId })
        .map((buffer) => {
          if (isCancelled) {
            return;
          }

          renderFrame({
            context,
            buffer,
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
          });

          setLastUpdatedAt(Date.now());
          setErrorMessage(undefined);
        })
        .mapErr((fetchError) => {
          console.error("Failed to update debug screen", fetchError);
          setErrorMessage("Unable to refresh screen preview");
        });
    };

    renderOnce();
    const intervalId = window.setInterval(renderOnce, REFRESH_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [props.publicDeviceId]);

  const statusText = (() => {
    if (errorMessage) {
      return <span className="text-destructive font-medium">{errorMessage}</span>;
    }

    if (lastUpdatedAt) {
      return <span>Last updated {new Date(lastUpdatedAt).toLocaleTimeString()}</span>;
    }

    return <span>Fetching preview…</span>;
  })();

  const headingText = props.heading ?? "Debug Screen";

  return (
    <main className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-6 px-6 py-10">
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="w-[400px] h-[400px] border-8 border-input bg-card shadow-2xl"
        style={{ imageRendering: "pixelated" }}
      />
    </main>
  );
};

